import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';

import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import { BarLoader } from 'react-spinners';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';

import i18n from './messages.json';
import { buildCatalog, getSegmentTree, getSelectableIds, getAtomicIds } from '../../services/campaign-catalog-service';

// Does this node (or any descendant) match the search query?
const nodeMatches = (node, q) => {
  if (!q) return true;
  if (node.label.toLowerCase().includes(q)) return true;

  return (node.children || []).some((c) => nodeMatches(c, q));
};

const formatReach = (n) => (typeof n === 'number' ? `(${n.toLocaleString('en-US')})` : '');

/**
 * Grouped, hierarchical Braze segment picker (WWW-15518, Figma 140-227).
 *
 * Rows are the *atomic* segments (General / Topics / Geo, nested). Selecting one
 * greys out any segment that can't form a combination with the current
 * selection — so the user can only build a set that maps to a real Braze
 * campaign. Confirms with the selected atomic campaign ids; NotificationForm
 * resolves them to the single (atomic or combined) campaign at send time.
 *
 * Props (drop-in compatible with the old CampaignPicker):
 *   show, onHide, onConfirm(ids), campaigns (raw Braze list), isLoading, selectedIds
 *   sizes: { [campaignId]: reachable-users estimate } — fetched once per session
 */
const SegmentPicker = ({ show, onHide, onConfirm, campaigns, isLoading, selectedIds, sizes = {} }) => {
  const modal = i18n.ink.notification.campaign.modal;

  const [draftIds, setDraftIds] = useState([]);
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState({});

  const catalog = useMemo(() => buildCatalog(campaigns || []), [campaigns]);
  const tree = useMemo(() => getSegmentTree(catalog), [catalog]);
  const atomicIds = useMemo(() => getAtomicIds(catalog), [catalog]);
  const selectable = useMemo(() => getSelectableIds(catalog, draftIds), [catalog, draftIds]);

  useEffect(() => {
    if (!show) return;

    setDraftIds(selectedIds || []);
    setSearch('');
    setCollapsed({});
  }, [show, selectedIds]);

  const q = search.trim().toLowerCase();

  const toggleNode = useCallback((id) => {
    setCollapsed((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const toggleSelect = useCallback((campaignId) => {
    setDraftIds((prev) =>
      prev.includes(campaignId) ? prev.filter((x) => x !== campaignId) : [...prev, campaignId]);
  }, []);

  const handleClear = useCallback(() => setDraftIds([]), []);

  const handleConfirm = useCallback(() => {
    onConfirm(draftIds);
    onHide();
  }, [onConfirm, onHide, draftIds]);

  const renderNode = (node, depth) => {
    if (!nodeMatches(node, q)) return null;

    const hasChildren = node.children && node.children.length > 0;
    // While searching, everything is force-expanded so matches deep in the tree
    // are visible.
    const isOpen = q ? true : !collapsed[node.id];
    const rows = [];

    const selected = node.selectable && draftIds.includes(node.campaignId);
    const disabled = node.selectable && !selected && !selectable.has(node.campaignId);

    rows.push(
      <div
        key={node.id}
        className={`segment-picker__row${disabled ? ' segment-picker__row--disabled' : ''}${selected ? ' segment-picker__row--selected' : ''}`}
        style={{ paddingLeft: `${16 + depth * 22}px` }}
      >
        {hasChildren ? (
          <button
            type='button'
            className='segment-picker__expand'
            aria-label={isOpen ? 'Collapse' : 'Expand'}
            onClick={() => toggleNode(node.id)}
            disabled={Boolean(q)}
          >
            <FontAwesomeIcon icon={isOpen ? faChevronDown : faChevronRight} />
          </button>
        ) : (
          <span className='segment-picker__expand segment-picker__expand--leaf' />
        )}

        {node.selectable ? (
          <label className='segment-picker__check'>
            <input
              type='checkbox'
              checked={selected}
              disabled={disabled}
              onChange={() => toggleSelect(node.campaignId)}
            />
            <span className='segment-picker__name'>{node.label}</span>
            <span className='segment-picker__reach'>{formatReach(sizes[node.campaignId])}</span>
          </label>
        ) : (
          <span className='segment-picker__name segment-picker__name--parent'>{node.label}</span>
        )}
      </div>,
    );

    if (hasChildren && isOpen) {
      node.children.forEach((child) => {
        const rendered = renderNode(child, depth + 1);

        if (rendered) rows.push(...rendered);
      });
    }

    return rows;
  };

  const renderBody = () => {
    if (isLoading) {
      return <div className='py-4 px-3'><BarLoader color='#3e75c8' height={2} width='100%' loading /></div>;
    }

    if (atomicIds.length === 0) {
      return <div className='segment-picker__empty'>{modal.noCampaigns}</div>;
    }

    const groups = tree
      .map((g) => ({ ...g, nodes: g.nodes.filter((n) => nodeMatches(n, q)) }))
      .filter((g) => g.nodes.length > 0);

    if (groups.length === 0) {
      return <div className='segment-picker__empty'>{modal.noResults}</div>;
    }

    return groups.map((g) => (
      <div key={g.group} className='segment-picker__group'>
        <div className='segment-picker__group-header'>{g.group}</div>
        {g.nodes.map((n) => renderNode(n, 0))}
      </div>
    ));
  };

  return (
    <Modal
      aria-labelledby='segment-picker-title'
      centered
      backdrop='static'
      onHide={onHide}
      show={show}
      size='lg'
      dialogClassName='segment-picker'
    >
      <Modal.Header closeButton>
        <Modal.Title id='segment-picker-title'>{modal.title}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className='segment-picker__search'>
          <FontAwesomeIcon icon={faMagnifyingGlass} className='segment-picker__search-icon' />
          <Form.Control
            type='search'
            placeholder={modal.searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
        </div>

        <div className='segment-picker__toolbar'>
          <span className='segment-picker__count'>
            <strong>{draftIds.length}</strong> {modal.selectedOf} {atomicIds.length} {modal.selected}
          </span>
          <button
            type='button'
            className='segment-picker__clear'
            onClick={handleClear}
            disabled={draftIds.length === 0}
          >
            {modal.clearAll}
          </button>
        </div>

        <div className='segment-picker__list'>{renderBody()}</div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant='light' onClick={onHide}>{i18n.ink.notification.buttons.cancel}</Button>
        <Button variant='primary' onClick={handleConfirm}>{i18n.ink.notification.buttons.confirm}</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default memo(SegmentPicker);
